import { imgixHost } from 'src/config'
import Circuit from 'src/models/circuit/Circuit'
import Model, { ID, Integer, IsoDate } from 'src/models/model'
import Site from 'src/models/site/Site'

export const REGIONS = {
  AU: 'AU',
  NZ: 'NZ',
  UK: 'UK',
  US: 'US',
  'US-BETA': 'US-BETA',
  'US-EAST': 'US-EAST',
  CB: 'CB',
  CINEPOLIS: 'CINEPOLIS',
  GTC: 'GTC'
}
REGIONS.ALL = Object.values(REGIONS)

export const DEVELOPER_ACTIONS = {
  DEPLOY_APPSTORE: 'deploy_appstore',
  RESEND_NUMERO: 'resend_numero',
  RESEND_MACCSBOX: 'resend_maccsbox',
  RESEND_RENTRAK: 'resend_rentrak',
  CREATE_CONSUMER_WEBSITE: 'create_consumer_website',
  CREATE_QR_CODE_SITE: 'create_qr_code_site',
  PROCESS_STRIPE_PAYMENT_LOGS: 'process_stripe_payment_logs',
  CLEAR_CLIENT_CONFIG_CACHE: 'clear_client_config_cache',
  CHANGE_LOG_LEVEL: 'change_log_level',
  REBUILD_JOURNAL_ENTRIES: 'rebuild_journal_entries',
  RUN_FILM_CHIEF_INTEGRATION: 'run_film_chief_integration',
  BUILD_CHART_OF_ACCOUNTS: 'build_chart_of_accounts',
  ENABLE_ACCESS_CONTROL_FOR_CURRENT_CIRCUIT: 'enable_access_control_for_circuit',
  DISABLE_ACCESS_CONTROL_FOR_CURRENT_CIRCUIT: 'disable_access_control_for_circuit',
  TRIGGER_PAGER: 'trigger_pager',
  CLEANUP_DUPLICATE_DATA_TABLES: 'cleanup_duplicate_data_tables'
}
DEVELOPER_ACTIONS.ALL = Object.values(DEVELOPER_ACTIONS)

export const DEVELOPER_INPUT_ACTIONS = {
  DAYS: [DEVELOPER_ACTIONS.RESEND_NUMERO, DEVELOPER_ACTIONS.RESEND_RENTRAK, DEVELOPER_ACTIONS.RESEND_MACCSBOX],
  ALL_SITES: [
    DEVELOPER_ACTIONS.RESEND_NUMERO, DEVELOPER_ACTIONS.RESEND_RENTRAK,
    DEVELOPER_ACTIONS.RESEND_MACCSBOX, DEVELOPER_ACTIONS.REBUILD_JOURNAL_ENTRIES
  ],
  DATE_RANGE: [DEVELOPER_ACTIONS.REBUILD_JOURNAL_ENTRIES],
  FORCE: [DEVELOPER_ACTIONS.REBUILD_JOURNAL_ENTRIES],
  DEPLOY_APPSTORE_INSTRUCTIONS: [DEVELOPER_ACTIONS.DEPLOY_APPSTORE],
  APP_STORE: [DEVELOPER_ACTIONS.DEPLOY_APPSTORE],
  APP_STORE_NAME: [DEVELOPER_ACTIONS.DEPLOY_APPSTORE],
  APP_STORE_BUNDLE_ID_SUFFIX: [DEVELOPER_ACTIONS.DEPLOY_APPSTORE],
  APP_VERSION: [DEVELOPER_ACTIONS.DEPLOY_APPSTORE],
  SITE_ID: [DEVELOPER_ACTIONS.DEPLOY_APPSTORE],
  NETLIFY_URL: [DEVELOPER_ACTIONS.DEPLOY_APPSTORE],
  IMGIX_HOST: [DEVELOPER_ACTIONS.DEPLOY_APPSTORE],
  USER_EMAIL: [DEVELOPER_ACTIONS.DEPLOY_APPSTORE],
  USER_FIRST_NAME: [DEVELOPER_ACTIONS.DEPLOY_APPSTORE],
  USER_LAST_NAME: [DEVELOPER_ACTIONS.DEPLOY_APPSTORE],
  USER_PHONE: [DEVELOPER_ACTIONS.DEPLOY_APPSTORE],
  LOG_LEVEL: [DEVELOPER_ACTIONS.CHANGE_LOG_LEVEL],
  YEAR: [DEVELOPER_ACTIONS.RUN_FILM_CHIEF_INTEGRATION],
  RECOGNITION_TYPES: [DEVELOPER_ACTIONS.BUILD_CHART_OF_ACCOUNTS],
  IS_CASHLESS: [DEVELOPER_ACTIONS.BUILD_CHART_OF_ACCOUNTS],
  SCREEN_NAME: [DEVELOPER_ACTIONS.TRIGGER_PAGER],
  POSITION: [DEVELOPER_ACTIONS.TRIGGER_PAGER],
  REMOVE_PAGER: [DEVELOPER_ACTIONS.TRIGGER_PAGER]
}

export default class Developer extends Model {
  static name = 'Developer'

  static noId = true

  static define () {
    this.fields = {
      netlifyConsumerTemplateApiKey: {
        type: String,
        output: true
      },
      netlifyPosApiKey: {
        type: String,
        output: true
      },
      netlifyAdminApiKey: {
        type: String,
        output: true
      },
      netlifySignageApiKey: {
        type: String,
        output: true
      },
      netlifyKioskApiKey: {
        type: String,
        output: true
      },
      netlifyKdsApiKey: {
        type: String,
        output: true
      },
      netlifyRoamApiKey: {
        type: String,
        output: true
      },
      sites: {
        type: [Site],
        output: true
      },

      circuitTemplates: {
        type: [Circuit],
        output: true
      },
      siteTemplates: {
        type: [Site],
        output: true
      },

      actionResponse: {
        type: String,
        output: true
      },

      newCircuitName: {
        type: String,
        input: true
      },
      newSiteName: {
        type: String,
        input: true
      },
      circuitTemplateId: {
        type: ID,
        input: true
      },
      siteTemplateId: {
        type: ID,
        input: true
      },
      createConsumerWebsite: {
        type: Boolean,
        input: true,
        default: true
      },

      action: {
        type: String,
        input: true,
        options: DEVELOPER_ACTIONS.ALL,
        default: null
      },
      days: {
        type: Integer,
        input: true
      },
      startDate: {
        type: IsoDate,
        input: true
      },
      endDate: {
        type: IsoDate,
        input: true
      },
      force: {
        type: Boolean,
        input: true,
        default: false
      },
      allSites: {
        type: Boolean,
        input: true,
        default: false
      },
      application: {
        type: String,
        input: true,
        default: null
      },

      appStore: {
        type: String,
        input: true,
        default: 'android',
        options: [
          'android',
          'ios'
        ]
      },
      appStoreName: {
        type: String,
        default: null
      },
      appStoreBundleIdSuffix: {
        type: String,
        default: null
      },
      appStoreVersion: {
        type: String,
        default: '0.0.1'
      },
      siteId: {
        type: ID,
        input: true,
        default: null
      },
      netlifySiteName: {
        type: String,
        output: true,
        default: null
      },
      netlifyUrl: {
        type: String,
        input: true,
        default: null
      },
      imgixHost: {
        type: String,
        input: true,
        default: imgixHost
      },
      userEmail: {
        type: String,
        input: true,
        default: null
      },
      userFirstName: {
        type: String,
        input: true,
        default: null
      },
      userLastName: {
        type: String,
        input: true,
        default: null
      },
      userPhone: {
        type: String,
        input: true,
        default: null
      },
      logLevel: {
        type: String,
        input: true,
        default: 'debug',
        options: [
          'debug',
          'info',
          'warn',
          'error',
          'fatal'
        ]
      },
      year: {
        type: String,
        input: true,
        default: null
      },
      recognitionType: {
        type: String,
        input: true,
        default: 'cash-basis',
        options: [
          'cash-basis',
          'accrual-basis'
        ]
      },
      isCashless: {
        type: Boolean,
        input: true,
        default: false
      },
      screenName: {
        type: String,
        input: true,
        default: null
      },
      position: {
        type: String,
        input: true,
        default: null
      },
      removePager: {
        type: Boolean,
        input: true,
        default: false
      }
    }
  }

  get circuitTemplateOptions () {
    return [
      { label: 'No Template, Start Blank', value: null },
      ..._.map(this.circuitTemplates.data, circuit => { return { label: circuit.name, value: circuit.id } })
    ]
  }

  get siteTemplateOptions () {
    return [
      { label: 'No Template, Start Blank', value: null },
      ..._.map(this.siteTemplates.data, site => { return { label: site.name, value: site.id } })
    ]
  }

  // The configuration objects are not loaded to be used above
  setAppStoreDefaults () {
    const currentSite = this.configuration?.currentSite
    const siteTitle = currentSite?.siteTitle || currentSite?.name || ''
    this.appStoreName = this.appStoreName || siteTitle.replace(/[^a-zA-Z0-9\s]/g, '') // Remove special characters
    this.appStoreBundleIdSuffix = this.appStoreBundleIdSuffix || this.netlifySiteName.replace(/[^a-z]/g, '')
    this.appStoreVersion = this.appStoreVersion || '0.0.1'
    this.siteId = this.configuration?.currentSite?.id
    this.netlifyUrl = this.configuration?.currentSite?.hostname
    this.userEmail = this.context?.currentUser?.email
    this.userFirstName = this.context?.currentUser?.firstName
    this.userLastName = this.context?.currentUser?.lastName
    this.userPhone = this.context?.currentUser?.phone
  }

  async doCreateConsumerWebsite () {
    this.action = DEVELOPER_ACTIONS.CREATE_CONSUMER_WEBSITE

    let result
    try {
      result = await this.save()
      if (result.errors.length) {
        console.log(result.errors)
        this.$mh.showErrors(result.errors)
      }
    } catch (error) {
      console.log(error)
      return false
    } finally {
      this.resetField('action')
    }

    return result.errors.length === 0
  }
}
